<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useQuasar } from 'quasar';
import { UserService } from '@/services';
import {
  InputMultiple,
  InputCheckboxTrueFalse,
  InputCheckboxChooseOne,
  InputSelect,
} from '@/components/inputs';
import Tr from '@/i18n/translation';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import {
  IRsvpAdditionalQuestionsLang,
  IRsvpPlacesLang,
  IRsvpWishesLang,
} from 'interface/data/rsvp/lang';
import { IRsvpAdditionalQuestionsCommon } from 'interface/data/rsvp/common';

const props = defineProps({
  clientId: {
    type: String,
    required: true,
  },
});

const quasar = useQuasar();
const rsvpStore = useRsvpStore();

const langList = Tr.supportedLocales;
const lang = ref(langList[0] ?? 'pl');

const additionalQuestionsNum = ref(
  rsvpStore.getAdditionalQuestions(lang.value).common.length,
);
const placesNum = ref(rsvpStore.getPlaces(lang.value).common.length);
const wishesNum = ref(rsvpStore.getWishes(lang.value).lang.length);

function addMissingRecords(getter, record) {
  const commonNumber = getter(lang.value).common.length;
  const langNumber = getter(lang.value).lang.length;
  for (let i = langNumber; i < commonNumber; ++i) {
    getter(lang.value).lang.push(structuredClone(record));
  }
}

function removeUnnecessaryRecords(getter) {
  const commonNumber = getter(lang.value).common.length;
  const langNumber = getter(lang.value).lang.length;

  if (commonNumber < langNumber) {
    const diff = langNumber - commonNumber;
    for (let i = 0; i < diff; ++i) {
      getter(lang.value).lang.pop();
    }
  }
}

watch(lang, async () => {
  rsvpStore.resetLangData(lang.value);
  await rsvpStore.tryGetLangData(lang.value);

  uploadRecords();
});

watch(
  () => rsvpStore.clientData.lang[lang.value],
  async () => {
    updateAdditionalQuestionsNumber();
    updatePlacesNumber();
    wishesNum.value = rsvpStore.getWishes(lang.value).lang.length;
  },
);

watch(wishesNum, async (number) => {
  if (number === undefined || isNaN(number) || number < 0 || number > 5) {
    wishesNum.value = 0;
  }

  if (number > rsvpStore.getWishes(lang.value).lang.length) {
    const num = number - rsvpStore.getWishes(lang.value).lang.length;
    for (let i = 0; i < num; ++i) {
      rsvpStore.getWishes(lang.value).lang.push({
        text: '',
      });
    }
  } else if (number < rsvpStore.getWishes(lang.value).lang.length) {
    const num = rsvpStore.getWishes(lang.value).lang.length - number;
    for (let i = 0; i < num; ++i) {
      rsvpStore.getWishes(lang.value).lang.pop();
    }
  }
});

function uploadRecords() {
  addMissingRecords(rsvpStore.getAdditionalQuestions, {
    header: '',
    selectOptions: [],
    optionTrue: '',
    optionFalse: '',
    placeholder: '',
  });
  addMissingRecords(rsvpStore.getPlaces, {
    name: '',
  });
  removeUnnecessaryRecords(rsvpStore.getAdditionalQuestions);
  removeUnnecessaryRecords(rsvpStore.getPlaces);

  updateAdditionalQuestionsNumber();
  updatePlacesNumber();
}

function updateAdditionalQuestionsNumber() {
  additionalQuestionsNum.value = rsvpStore.getAdditionalQuestions(
    lang.value,
  ).lang.length;
}

function updatePlacesNumber() {
  placesNum.value = rsvpStore.getPlaces(lang.value).lang.length;
}

async function sendRsvpData() {
  if (props.clientId) {
    const res = await UserService.sendRsvpLangData(
      props.clientId,
      rsvpStore.clientData.lang[lang.value],
      lang.value,
    );
    if (res) {
      quasar.notify({
        type: 'info',
        message: `Wysłano dane klienta`,
      });
    } else {
      quasar.notify({
        type: 'negative',
        message: `Błąd w przesyle danych`,
      });
    }
  } else {
    quasar.notify({
      type: 'negative',
      message: `Nie wybrano wszystkich pól`,
    });
  }
}

function getAdditionalQuestionsCommon(i: number) {
  let data = rsvpStore.getAdditionalQuestionsById(i, lang.value).common;
  if (!data) {
    uploadRecords();
    data = rsvpStore.getAdditionalQuestionsById(i, lang.value).common;
  }

  return data as IRsvpAdditionalQuestionsCommon;
}

function getAdditionalQuestionsLang(i: number) {
  let data = rsvpStore.getAdditionalQuestionsById(i, lang.value).lang;
  if (!data) {
    uploadRecords();
    data = rsvpStore.getAdditionalQuestionsById(i, lang.value).lang;
  }

  return data as IRsvpAdditionalQuestionsLang;
}

function getPlaces(i: number) {
  let data = rsvpStore.getPlacesById(i, lang.value).lang;
  if (!data) {
    uploadRecords();
    data = rsvpStore.getPlacesById(i, lang.value).lang;
  }

  return data as IRsvpPlacesLang;
}

function getWishes(i: number) {
  let data = rsvpStore.getWishById(i, lang.value).lang;
  if (!data) {
    uploadRecords();
    data = rsvpStore.getWishById(i, lang.value).lang;
  }

  return data as IRsvpWishesLang;
}

onMounted(() => {
  uploadRecords();
});
</script>

<template>
  <q-form ref="ClientForm" class="form" @submit.prevent="sendRsvpData">
    <div class="send">
      <button class="core-default-form-button" type="submit">Wyślij</button>
    </div>
    <InputSelect
      v-model="lang"
      class="input"
      :select-options="langList"
      main-color="#84491f"
      background-color="#fffbf8"
      text-font="Raleway"
      >Język</InputSelect
    >
    <div class="inputs">
      <div class="input">
        <p class="label">Tekst zaproszenia</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).invitationText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst na co jest zaproszenie</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).invitationPurposeText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst nad imionami</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).upperHeaderText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst zamiast daty</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).yearText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst pod formularzem</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).bottomText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst zamiast informacji o dacie i miejscu wesela</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).moreInfoText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst zamiast daty na dole strony</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).bottomYearText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Miejsce ślubu</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).place"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Do kiedy potwierdzić obecność</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).formattedDueToText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Dodatkowe informacje</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).additionalInformation"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Dodatkowe informacje po drugim miejscu</p>
        <q-input
          v-model="
            rsvpStore.getLangAllLangData(lang).additionalSecondInformation
          "
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst nagłówka formularza</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).form.confirmArrival"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Tekst pod nagłówkiem formularza</p>
        <q-input
          v-model="rsvpStore.getLangAllLangData(lang).form.arrivalText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div>
        <div v-if="placesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 3"
          v-show="i <= placesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= placesNum" class="row-area">
            <div class="input">
              <p class="label">Nazwa miejsca</p>
              <q-input
                v-model="getPlaces(i - 1).name"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
            <div class="input">
              <p class="label">Lokalizacja miejsca</p>
              <q-input
                v-model="getPlaces(i - 1).location"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="additionalQuestionsNum !== 0"
          class="row-area-container"
        ></div>
        <div
          v-for="i in 10"
          v-show="i <= additionalQuestionsNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= additionalQuestionsNum" class="row-area">
            <div class="input">
              <p class="label">Nagłówek *</p>
              <q-input
                v-model="getAdditionalQuestionsLang(i - 1).header"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
            <div class="input">
              <p class="label">Dodatkowe informacje</p>
              <q-input
                v-model="getAdditionalQuestionsLang(i - 1).info"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
              />
            </div>
            <div class="input">
              <p class="label">Zapytanie</p>
              <q-input
                v-model="getAdditionalQuestionsLang(i - 1).querry"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
              />
            </div>
            <div
              v-if="
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputSelect' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputSelectMultiple' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseSelect'
              "
              class="row-area"
            >
              <InputMultiple
                v-model="getAdditionalQuestionsLang(i - 1).selectOptions"
                :items="[]"
                class="input"
                main-color="#84491f"
                background-color="#fffbf8"
                text-font="Raleway"
                >Opcje selecta</InputMultiple
              >
              <InputSelect
                v-model="getAdditionalQuestionsLang(i - 1).default"
                class="input"
                :select-options="
                  getAdditionalQuestionsLang(i - 1).selectOptions
                "
                main-color="#84491f"
                background-color="#fffbf8"
                text-font="Raleway"
                >Domyślna wartość</InputSelect
              >
            </div>
            <div
              v-if="
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalse' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseSelect' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseTextbox' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseInfo' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxChooseOne'
              "
              class="row-area"
            >
              <div class="input">
                <p class="label">Opcja 1</p>
                <q-input
                  v-model="getAdditionalQuestionsLang(i - 1).optionTrue"
                  class="core-default-input-form"
                  borderless
                  autogrow
                />
              </div>
              <div class="input">
                <p class="label">Opcja 2</p>
                <q-input
                  v-model="getAdditionalQuestionsLang(i - 1).optionFalse"
                  class="core-default-input-form"
                  borderless
                  autogrow
                />
              </div>
              <InputCheckboxTrueFalse
                v-if="
                  getAdditionalQuestionsCommon(i - 1).inputType ===
                    'InputCheckboxTrueFalse' ||
                  getAdditionalQuestionsCommon(i - 1).inputType ===
                    'InputCheckboxTrueFalseTextbox' ||
                  getAdditionalQuestionsCommon(i - 1).inputType ===
                    'InputCheckboxTrueFalseSelect' ||
                  getAdditionalQuestionsCommon(i - 1).inputType ===
                    'InputCheckboxTrueFalseInfo'
                "
                v-model="
                  getAdditionalQuestionsLang(i - 1)
                    .default as unknown as boolean
                "
                class="input"
                option-true="Opcja 1 ( TRUE )"
                option-false="Opcja 2 ( FALSE )"
                main-color="#84491f"
                background-color="#fffbf8"
                text-font="Raleway"
                >Domyślna wartość
              </InputCheckboxTrueFalse>
              <InputCheckboxChooseOne
                v-if="
                  getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxChooseOne'
                "
                v-model="getAdditionalQuestionsLang(i - 1).default"
                class="input"
                :option-true="getAdditionalQuestionsLang(i - 1).optionTrue"
                :option-false="getAdditionalQuestionsLang(i - 1).optionFalse"
                main-color="#84491f"
                background-color="#fffbf8"
                text-font="Raleway"
                >Domyślna wartość
              </InputCheckboxChooseOne>
            </div>
            <div
              v-if="
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputTextbox' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseTextbox' ||
                getAdditionalQuestionsCommon(i - 1).inputType ===
                  'InputCheckboxTrueFalseInfo'
              "
              class="row-area"
            >
              <div class="input">
                <p class="label">Placeholder</p>
                <q-input
                  v-model="getAdditionalQuestionsLang(i - 1).placeholder"
                  class="core-default-input-form"
                  borderless
                  autogrow
                />
              </div>
              <div class="input">
                <p class="label">Domyślna wartość</p>
                <q-input
                  v-model="getAdditionalQuestionsLang(i - 1).default"
                  class="core-default-input-form"
                  borderless
                  autogrow
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="input">
          <p class="label">Liczba dodatkowych życzeń</p>
          <div class="slider">
            <q-slider
              v-model="wishesNum"
              class="core-default-input-form slider-form"
              :min="0"
              :max="5"
              label
              switch-label-side
              color="primary"
              track-color="accent"
              thumb-color="accent"
              label-color="accent"
            />
            <q-input
              v-model="wishesNum"
              class="core-default-input-form"
              borderless
              autogrow
            />
          </div>
        </div>
        <div v-if="wishesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 5"
          v-show="i <= wishesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= wishesNum" class="row-area">
            <div class="input">
              <p class="label">Tekst</p>
              <q-input
                v-model="getWishes(i - 1).text"
                class="core-default-input-form"
                borderless
                autogrow
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-form>
</template>

<style lang="scss" scoped>
@import '@/components/admin/form';
</style>
